import { useState } from 'react';
import { deserialize } from 'serializr';
import { generatePath } from 'react-router-dom';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from './../../routes/routeConstants/apiRoutes';
import { ProductEmission } from 'models/ProductEmission/productEmission.model';
import { TotalProductEmissionAnnual } from 'models/TotalProductEmissionAnnual/totalProductEmissionAnnual.model';
import { PaginationConfig } from 'models/PaginationConfig/paginationConfig.model';
import { contentDispositionExtraction } from 'shared/utils/contentDispositionExtraction';
import { DQRMetrics } from 'models/Dqr/dqr.model';
import { Metrics } from 'models/Dqr/metrics.model';
import { ActivityData } from 'models/ActivityData';

export interface DashboardGoalInterface {
  type?: string;
  dashboard_id?: number | undefined;
  co2e: number | undefined;
}
export interface EmissionParams {
  business_model: string;
  page?: number;
  is_asc?: boolean;
}

export type DqrData = {
  dqrMetrics: Metrics | null;
  totalScore: number;
};

export interface GoalProduct {
  co2e: number | undefined;
}

const DashboardService = () => {
  const [productEmissionList, setProductEmissionList] = useState<
    ProductEmission[]
  >([]);

  const [productEmissonAnnualList, setProductEmissionAnnualList] = useState<
    TotalProductEmissionAnnual[]
  >([]);

  const [productEmissionLoading, setProductEmissionLoading] =
    useState<boolean>(false);

  const [productEmissionPagination, setProductEmissionPagination] =
    useState<PaginationConfig>({});

  const [productEmissionAnnualLoading, setProductAnnualEmissionLoading] =
    useState<boolean>(false);

  const [productEmissionAnnualPagination, setProductEmissionAnnualPagination] =
    useState<PaginationConfig>({});

  const [dqrData, setDqrData] = useState<DqrData>({
    dqrMetrics: null,
    totalScore: 1,
  });

  const [activityMapping, setActivityMapping] = useState<ActivityData[]>([]);
  const [goalProduct, setGoalProduct] = useState<GoalProduct | null>(null);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  /** fetch product emission  */

  const fetchProductEmission = async (params: EmissionParams) => {
    try {
      setProductEmissionLoading(true);
      const url = generatePath(ApiRoutes.FETCH_PRODUCT_EMISSION);
      const response = await axiosInstance.get(url, { params });
      const data = deserialize(
        ProductEmission,
        response.data.data
      ) as ProductEmission[];
      setProductEmissionPagination(response.data);
      setProductEmissionList(data);
    } catch (error) {
    } finally {
      setProductEmissionLoading(false);
    }
  };

  /**fetch product emission annual */
  const fetchProductEmissionAnnual = async (params: {
    business_model: string;
    page?: number;
  }) => {
    try {
      setProductAnnualEmissionLoading(true);
      const url = generatePath(ApiRoutes.FETCH_PRODUCT_EMISSION_ANNUAL);
      const response = await axiosInstance.get(url, { params });
      const data = deserialize(
        TotalProductEmissionAnnual,
        response.data.data
      ) as TotalProductEmissionAnnual[];
      setProductEmissionAnnualPagination(response.data);
      setProductEmissionAnnualList(data);
    } catch (error) {
    } finally {
      setProductAnnualEmissionLoading(false);
    }
  };

  const downloadGraph = async (url?: string, params?: EmissionParams) => {
    try {
      const downloadUrl = generatePath(url + ApiRoutes.DOWNLOAD);
      const response = await axiosInstance.get(downloadUrl, { responseType: 'blob', params });
      const blob = new Blob([response.data]);
      const fileName = contentDispositionExtraction(response);
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
    }
  };

  const downloadGraphCsv = async (id?: string) => {
    try {
      setProductEmissionLoading(true);
      const downloadUrl = generatePath(ApiRoutes.DOWNLOAD_MAP_CSV, { id });
      const response = await axiosInstance.get(downloadUrl, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const fileName = contentDispositionExtraction(response);
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
    } finally {
      setProductEmissionLoading(false);
    }
  };

  const fetchDqr = async (id: number) => {
    try {
      setProductEmissionLoading(true);
      const url = generatePath(ApiRoutes.FETCH_DQR, { id });
      const response = await axiosInstance.get(url);
      const data = deserialize(DQRMetrics, response.data);
      if (data && data.dqrMetrics && data.totalScore !== undefined) {
        setDqrData({
          dqrMetrics: data.dqrMetrics,
          totalScore: data.totalScore,
        });
      }
    } catch (error) {
    } finally {
      setProductEmissionLoading(false);
    }
  };

  const fetchActivityMapping = async (params?: { activity_label?: string }) => {
    try {
      setProductEmissionLoading(true);
      const response = await axiosInstance.get(ApiRoutes.META_ACTIVITY, {
        params,
      });
      const data: ActivityData[] = deserialize(ActivityData, response.data) as ActivityData[];
      setActivityMapping(data);
    } catch (error) {
    } finally {
      setProductEmissionLoading(false);
    }
  };

  const viewApprovalReport = async (id: number | undefined | string) => {
    try {
      setProductEmissionLoading(true);
      const url = generatePath(ApiRoutes.VIEW_REPORT, { id });
      const response = await axiosInstance.get(url);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error(error, 'error');
    } finally {
      setProductEmissionLoading(false);
    }
  };

  const fetchGoalProduct = async (params?: {
    type?: string;
    dashboard_id?: number;
  }) => {
    try {
      setButtonLoading(true);
      const response = await axiosInstance.get(ApiRoutes.FETCH_GOAL, {
        params,
      });
      const { data } = response;
      setGoalProduct(data);
    } catch (error) {
      // Handle error if needed
    } finally {
      setButtonLoading(false);
    }
  };


  const setGoal = async (goal: DashboardGoalInterface) => {
    try {
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.SET_GOAL, goal);
      await fetchGoalProduct({
        type: goal.type,
        dashboard_id: goal.dashboard_id,
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  const updateGoal = async (goal: DashboardGoalInterface) => {
    try {
      setButtonLoading(true);
      await axiosInstance.put(ApiRoutes.UPDATE_GOAL, goal);
      await fetchGoalProduct({
        type: goal.type,
        dashboard_id: goal.dashboard_id,
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  return {
    fetchProductEmission,
    productEmissionList,
    fetchProductEmissionAnnual,
    productEmissonAnnualList,
    productEmissionLoading,
    productEmissionPagination,
    productEmissionAnnualLoading,
    productEmissionAnnualPagination,
    downloadGraph,
    downloadGraphCsv,
    fetchDqr,
    dqrData,
    fetchActivityMapping,
    activityMapping,
    viewApprovalReport,
    fetchGoalProduct,
    goalProduct,
    setGoal,
    updateGoal,
    buttonLoading
  };
};

export default DashboardService;
